import { lazy } from 'react'
import { Route, Redirect } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { SecureRoute } from '@okta/okta-react'

import { AuthorizationRoles, AuthPermissions } from '../Utilities/enum'
import { useAppState } from '../Context/AppProvider'
import AuthorizedView from './AuthorizedView'
import UnauthorizedPage from '../Pages/UnauthorizedPage'
import TaxesPage from '../Pages/Taxes'

const CustomerSearchPage = lazy(() => import('../Pages/Customers.react'))
const CustomerRoutes = lazy(() => import('./CustomerRoutes'))
const ReconcileTransactionsPage = lazy(() =>
  import('../Pages/ReconcileTransactionsPage')
)

const CustomReportsPage = lazy(() => import('../Pages/CustomReportsPage.react'))
const HoldingsPage = lazy(() => import('../Pages/Holdings'))
const TransactionDetails = lazy(() => import('../Pages/TransactionDetails'))
const EnterpriseTransactions = lazy(() =>
  import('../Pages/EnterpriseTransactions')
)
const ReportsPage = lazy(() => import('../Pages/Reports'))
const Dashboard = lazy(() => import('../Pages/Dashboard.react'))
const SettingsPage = lazy(() => import('../Pages/SettingsPage.react'))
const CustomUmcReportsPage = lazy(() => import('../Pages/CustomUmcReports'))

const handleRoot = (appState, role, cmsSection, casSection, permissions) => {
  if (!appState.tenant.id) {
    return <Redirect to="/unauthorized" />
  }

  const isPortalAdmin = role === AuthorizationRoles.cmsPortalAdmin
  const showUMC = permissions.includes(AuthPermissions.customerRead)
  const showTransactions = permissions.includes(
    AuthPermissions.reconciliationRead
  )

  if (isPortalAdmin) {
    return <Redirect to="/user-management" />
  } else if (showUMC && cmsSection) {
    return <Redirect to="/customers" />
  } else if (casSection) {
    if (showTransactions) return <Redirect to="/transactions" />
    return <Redirect to="/dashboard" />
  }
  return <Redirect to="/unauthorized" />
}

const Routes = () => {
  const {
    casSection,
    cmsSection,
    casUnreconciledTransactions,
    umcCustomReports,
    umcW8W9Reports,
    automaticTinMatching,
  } = useFlags()
  const appState = useAppState()
  const clientRole = appState.role
  const clientPermissions = appState.permissions
  const authorizedDefaults = {
    clientPermissions,
    failureRedirect: '/unauthorized',
    tenantId: appState.tenant.id,
  }

  return (
    <div className={'content-container'}>
      <Route exact path={'/'}>
        <h1>Auth</h1>
        {handleRoot(
          appState,
          clientRole,
          cmsSection,
          casSection,
          clientPermissions
        )}
      </Route>
      <Route path={'/unauthorized'}>
        <UnauthorizedPage />
      </Route>
      <SecureRoute exact path={'/customers'}>
        <AuthorizedView
          {...authorizedDefaults}
          isFeatureFlagPresent={cmsSection}
          authorizedPermissions={[AuthPermissions.customerRead]}
        >
          <CustomerSearchPage />
        </AuthorizedView>
      </SecureRoute>
      <SecureRoute path={'/customers/:customerUUID'}>
        <AuthorizedView
          {...authorizedDefaults}
          isFeatureFlagPresent={cmsSection}
          authorizedPermissions={[AuthPermissions.customerRead]}
        >
          <CustomerRoutes />
        </AuthorizedView>
      </SecureRoute>
      <SecureRoute path={'/customer-reports'}>
        <AuthorizedView
          {...authorizedDefaults}
          isFeatureFlagPresent={
            cmsSection &&
            (umcCustomReports || umcW8W9Reports || automaticTinMatching)
          }
          authorizedPermissions={[AuthPermissions.customerPiiRead]}
        >
          <CustomUmcReportsPage />
        </AuthorizedView>
      </SecureRoute>
      <SecureRoute path={'/user-management'}>
        <AuthorizedView
          {...authorizedDefaults}
          authorizedPermissions={[AuthPermissions.userRead]}
        >
          <SettingsPage />
        </AuthorizedView>
      </SecureRoute>
      <SecureRoute exact path={'/dashboard'}>
        <AuthorizedView
          {...authorizedDefaults}
          isFeatureFlagPresent={casSection}
        >
          <Dashboard />
        </AuthorizedView>
      </SecureRoute>
      <SecureRoute exact path={'/holdings'}>
        <AuthorizedView
          {...authorizedDefaults}
          isFeatureFlagPresent={casSection}
          authorizedPermissions={[AuthPermissions.reportsRead]}
        >
          <HoldingsPage />
        </AuthorizedView>
      </SecureRoute>
      <SecureRoute exact path={'/reports'}>
        <AuthorizedView
          {...authorizedDefaults}
          isFeatureFlagPresent={casSection}
          authorizedPermissions={[AuthPermissions.reportsRead]}
        >
          <ReportsPage />
        </AuthorizedView>
      </SecureRoute>
      <SecureRoute exact path={'/reports/custom'}>
        <AuthorizedView
          {...authorizedDefaults}
          authorizedPermissions={[AuthPermissions.tenantRead]}
        >
          <CustomReportsPage />
        </AuthorizedView>
      </SecureRoute>
      <SecureRoute exact path={'/transactions'}>
        <AuthorizedView
          {...authorizedDefaults}
          isFeatureFlagPresent={casSection}
          authorizedPermissions={[AuthPermissions.reconciliationRead]}
        >
          <EnterpriseTransactions />
        </AuthorizedView>
      </SecureRoute>
      <SecureRoute path={'/transactions/:transactionUUID'}>
        <AuthorizedView
          {...authorizedDefaults}
          isFeatureFlagPresent={casSection}
          authorizedPermissions={[AuthPermissions.reconciliationRead]}
        >
          <TransactionDetails />
        </AuthorizedView>
      </SecureRoute>
      <SecureRoute path={'/taxes'}>
        <AuthorizedView
          {...authorizedDefaults}
          isFeatureFlagPresent={casSection}
          authorizedPermissions={[AuthPermissions.reportsRead]}
        >
          <TaxesPage />
        </AuthorizedView>
      </SecureRoute>
      <SecureRoute path={'/reconcile-transactions'}>
        <AuthorizedView
          {...authorizedDefaults}
          isFeatureFlagPresent={casSection && casUnreconciledTransactions}
        >
          <ReconcileTransactionsPage />
        </AuthorizedView>
      </SecureRoute>
    </div>
  )
}

export default Routes
